import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleLanding from "./components/RoleLanding";
import AdminDashboard from "./components/Admin/AdminDashboard";
import PatientManagement from "./components/Admin/PatientManagement";
import PatientDetail from "./components/Admin/PatientDetail";
import PatientStats from "./components/Admin/PatientStats";
import PatientGameSettings from "./components/Admin/PatientGameSettings";
import PatientStatistics from "./components/Admin/PatientStatistics";
import MyStatsPage from "./components/Patient/MyStatsPage";
import SharedStatsPage from "./components/Patient/SharedStatsPage";
import ProviderAuthPage from "./components/CareTeam/provider/ProviderAuthPage";
import ProviderShell from "./components/CareTeam/provider/ProviderShell";
import RosterPage from "./components/CareTeam/provider/RosterPage";
import PatientDetailPage from "./components/CareTeam/provider/PatientDetailPage";
import CareTeamPage from "./components/CareTeam/player/CareTeamPage";
import JoinPage from "./components/CareTeam/player/JoinPage";
import { useClockProgression } from "./components/TimeMatch/useClockProgression";
import MatchingGameForm from "./components/Matching/MatchingGameForm";
import SerialSubtractionForm from "./components/SerialSubtraction/SerialSubtractionForm";
import PricingPage from "./components/Payments/PricingPage";
import CheckoutSuccessPage from "./components/Payments/CheckoutSuccessPage";
import CheckoutCancelPage from "./components/Payments/CheckoutCancelPage";
import GameGate from "./components/Payments/GameGate";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import { getLoggedInUserString } from "./components/utils.js";
import RegisterPage from "./components/User/RegisterPage/RegisterPage";
import SignInPage from "./components/User/SignInPage/SignInPage";
import ActivatePage from "./components/User/ActivatePage/ActivatePage";
import VerifyEmailPage from "./components/User/VerifyEmailPage/VerifyEmailPage";
import ResetPasswordConfirmPage from "./components/User/ResetPasswordConfirmPage/ResetPasswordConfirmPage";
import SettingsPage from "./components/User/SettingsPage/SettingsPage";
import Home from "./components/Home";
import ContactPage from "./components/Contact/ContactPage";
import SnakeGameForm from "./components/Snake/SnakeGameForm";
import MemoryMatrixForm from "./components/MemoryMatrix/MemoryMatrixForm";
import CardsMatchUpForm from "./components/CardsMatchUp/CardsMatchUpForm";
import BookClubGameForm from "./components/BookClub/BookClubGameForm";
import ButterflyPathForm from "./components/ButterflyPath/ButterflyPathForm";
import AnalogToDigitalForm from "./components/TimeMatch/AnalogToDigitalForm";
import DigitalToAnalogForm from "./components/TimeMatch/DigitalToAnalogForm";
import FindMiddleForm from "./components/FindMiddle/FindMiddleForm.js";
import ConnectTheDotsForm from "./components/ConnectTheDots/ConnectTheDotsForm.js";
import Footer from "./components/Footer/Footer.js";
import usePageTracking from "./usePageTracking";

import "./components/common.css";

// The provider portal brings its own header, so it skips the app's chrome.
const Layout = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/provider")) {
    return <div className="app-shell">{children}</div>;
  }
  return (
    <div className="app-shell">
      <Navbar />
      <main className="app-shell__main">{children}</main>
      <Footer />
    </div>
  );
};

const SnakeGameFormWrapper = () => {
  const location = useLocation();
  const levelParams = location.state ? JSON.parse(location.state) : {};
  return <SnakeGameForm {...levelParams} />;
};

const MemoryMatrixFormWrapper = () => {
  const location = useLocation();
  const levelParams = location.state ? JSON.parse(location.state) : {};
  return <MemoryMatrixForm {...levelParams} />;
};

const AnalogToDigitalFormWrapper = () => {
  const location = useLocation();
  const levelParams = location.state ? JSON.parse(location.state) : {};
  const progression = useClockProgression("time-match-analog-to-digital");
  return <AnalogToDigitalForm {...levelParams} progression={progression} />;
};

const DigitalToAnalogFormWrapper = () => {
  const location = useLocation();
  const levelParams = location.state ? JSON.parse(location.state) : {};
  const progression = useClockProgression("time-match-digital-to-analog");
  return <DigitalToAnalogForm {...levelParams} progression={progression} />;
};

  const FindMiddleFormWrapper = () => {
    const location = useLocation();
    const levelParams = location.state ? JSON.parse(location.state) : {};
    return <FindMiddleForm {...levelParams} />;
  };

const ConnectTheDotsFormWrapper = () => {
  const location = useLocation();
  const levelParams = location.state ? JSON.parse(location.state) : {};
  return <ConnectTheDotsForm {...levelParams} />;
};

const AppContent = () => {
  usePageTracking();
  return (
    <AuthProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<RoleLanding />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireRole="clinician">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stats"
            element={
              <ProtectedRoute requireRole="clinician">
                <PatientStatistics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/patients"
            element={
              <ProtectedRoute requireRole="clinician">
                <PatientManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/patients/:id"
            element={
              <ProtectedRoute requireRole="clinician">
                <PatientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/patients/:id/stats"
            element={
              <ProtectedRoute requireRole="clinician">
                <PatientStats />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/patients/:id/settings"
            element={
              <ProtectedRoute requireRole="clinician">
                <PatientGameSettings />
              </ProtectedRoute>
            }
          />
          {/* Care team: provider portal. */}
          <Route
            path="/provider/signin"
            element={<ProviderAuthPage mode="signin" />}
          />
          {/* Old addresses, kept so earlier links still work. */}
          <Route
            path="/provider/sign-in"
            element={<Navigate to="/provider/signin" replace />}
          />
          <Route
            path="/provider/signup"
            element={<ProviderAuthPage mode="signup" />}
          />
          <Route
            path="/provider/sign-up"
            element={<Navigate to="/provider/signup" replace />}
          />
          <Route
            path="/provider"
            element={
              <ProtectedRoute
                requireRole="clinician"
                signInPath="/provider/signin"
              >
                <ProviderShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="patients" replace />} />
            <Route path="patients" element={<RosterPage />} />
            <Route path="patients/:id" element={<PatientDetailPage />} />
          </Route>
          {/* Care team: the player's side. */}
          <Route
            path="/settings/care-team"
            element={
              <ProtectedRoute requireRole="patient">
                <CareTeamPage />
              </ProtectedRoute>
            }
          />
          <Route path="/join/:code" element={<JoinPage />} />
          {/* Links in activation emails sent before sign-up stopped waiting for them. */}
          <Route path="/activate/:uid/:token" element={<ActivatePage />} />
          <Route path="/verify-email/:uid/:token" element={<VerifyEmailPage />} />
          <Route
            path="/reset-password-confirm/:uid/:token"
            element={<ResetPasswordConfirmPage />}
          />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/stats"
            element={
              <ProtectedRoute>
                <MyStatsPage />
              </ProtectedRoute>
            }
          />
          {/* Public read-only stats, opened from a patient's share link. */}
          <Route path="/shared/:token" element={<SharedStatsPage />} />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <PricingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/success"
            element={
              <ProtectedRoute>
                <CheckoutSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
          <Route path="/home" element={<Home />} />
          <Route path="/contact" element={<ContactPage />} />

          <Route
            path="/snake-game"
            element={
              <GameGate>
                <SnakeGameFormWrapper />
              </GameGate>
            }
          />
          <Route
            path="/memory-matrix"
            element={
              <GameGate>
                <MemoryMatrixFormWrapper />
              </GameGate>
            }
          />
          <Route
            path="/cards-match-up"
            element={
              <GameGate>
                <CardsMatchUpForm
                  initialFruitCount={5}
                  timeToMemorize={10}
                  initialPuzzleCount={10}
                />
              </GameGate>
            }
          />
          <Route
            path="/book-club"
            element={
              <GameGate>
                <BookClubGameForm />
              </GameGate>
            }
          />
          <Route
            path="/butterfly-path"
            element={
              <GameGate>
                <ButterflyPathForm />
              </GameGate>
            }
          />
          <Route
            path="/time-match-analog-to-digital"
            element={
              <GameGate>
                <AnalogToDigitalFormWrapper />
              </GameGate>
            }
          />
          <Route
            path="/time-match-digital-to-analog"
            element={
              <GameGate>
                <DigitalToAnalogFormWrapper />
              </GameGate>
            }
          />
          <Route
            path="/find-the-middle"
            element={
              <GameGate>
                <FindMiddleFormWrapper />
              </GameGate>
            }
          />
          <Route
            path="/connect-the-dots"
            element={
              <GameGate>
                <ConnectTheDotsFormWrapper />
              </GameGate>
            }
          />
          <Route
            path="/image-to-word"
            element={
              <GameGate>
                <MatchingGameForm
                  mode="image-to-word"
                  gameRoutePath="image-to-word"
                  title="Pick the word"
                />
              </GameGate>
            }
          />
          <Route
            path="/word-to-image"
            element={
              <GameGate>
                <MatchingGameForm
                  mode="word-to-image"
                  gameRoutePath="word-to-image"
                  title="Pick the picture"
                />
              </GameGate>
            }
          />
          <Route
            path="/serial-subtraction"
            element={
              <GameGate>
                <SerialSubtractionForm />
              </GameGate>
            }
          />
          <Route
            path="/voice-image-recognition"
            element={
              <GameGate>
                <MatchingGameForm
                  mode="word-to-image"
                  speakTarget
                  gameRoutePath="voice-image-recognition"
                  title="Listen and pick the picture"
                />
              </GameGate>
            }
          />
        </Routes>
      </Layout>
    </AuthProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <Router>
      <AppContent />
    </Router>
  </HelmetProvider>
);

export default App;
